import { create } from "zustand";
import { persist } from "zustand/middleware";
import { addToCartAPI, removeFromCartAPI, updateCartAPI, fetchCartAPI, syncCartAPI } from "../Services/userService";
import useAuthStore from "./AuthStore";

const useCartStore = create(
    persist(
        (set, get) => ({
            cart: [],
            isCartOpen: false,
            openCart: () => set({ isCartOpen: true }),
            closeCart: () => set({ isCartOpen: false }),

            fetchCart: async () => {
                const { isAuthenticated } = useAuthStore.getState();
                if (!isAuthenticated) return;

                const response = await fetchCartAPI();
                if (response.success && response.data) {
                    // Map backend data (giftId/gift) to frontend format
                    const mappedCart = response.data.map(item => {
                        const gift = item.gift || {};
                        return {
                            ...gift,
                            id: item.giftId,
                            image: gift.image || gift.img, // Sync img/image field names
                            quantity: item.quantity
                        };
                    });
                    set({ cart: mappedCart });
                }
            },

            syncCart: async () => {
                const { isAuthenticated } = useAuthStore.getState();
                if (!isAuthenticated) return;

                const { cart } = get();
                if (cart.length === 0) return;

                const cartData = cart.map(item => ({
                    giftId: item.id || item._id, // Support both formats
                    quantity: item.quantity
                }));

                await syncCartAPI(cartData);
            },

            addToCart: async (product) => {
                const { cart } = get();
                const productId = product.id || product._id;
                const existingItem = cart.find((item) => (item.id || item._id) === productId);

                let newCart;
                if (existingItem) {
                    newCart = cart.map((item) =>
                        (item.id || item._id) === productId ? { ...item, quantity: item.quantity + 1 } : item
                    );
                } else {
                    newCart = [...cart, { ...product, quantity: 1, id: productId }];
                }

                set({ cart: newCart });
                get().openCart();

                const { isAuthenticated } = useAuthStore.getState();
                if (isAuthenticated) {
                    await addToCartAPI(productId, 1);
                }
            },

            removeFromCart: async (productId) => {
                const { cart } = get();
                const newCart = cart.filter((item) => (item.id || item._id) !== productId);
                set({ cart: newCart });

                const { isAuthenticated } = useAuthStore.getState();
                if (isAuthenticated) {
                    await removeFromCartAPI(productId);
                }
            },

            updateQuantity: async (productId, quantity) => {
                const { cart } = get();
                const newCart = cart
                    .map((item) =>
                        (item.id || item._id) === productId ? { ...item, quantity: Math.max(0, quantity) } : item
                    )
                    .filter((item) => item.quantity > 0);

                set({ cart: newCart });

                const { isAuthenticated } = useAuthStore.getState();
                if (isAuthenticated) {
                    const cartData = newCart.map(item => ({
                        giftId: item.id || item._id,
                        quantity: item.quantity
                    }));
                    await updateCartAPI(cartData);
                }
            },

            clearCart: async () => {
                set({ cart: [] });
                const { isAuthenticated } = useAuthStore.getState();
                if (isAuthenticated) {
                    await updateCartAPI([]);
                }
            },

            getCartCount: () => {
                const { cart } = get();
                return cart.reduce((total, item) => total + item.quantity, 0);
            },

            getCartTotal: () => {
                const { cart } = get();
                return cart.reduce((total, item) => total + (Number(item.price) || 0) * item.quantity, 0);
            },
        }),
        {
            name: "cart-storage",
            onRehydrateStorage: (state) => {
                return (state, error) => {
                    if (state && !error) {
                        state.fetchCart();
                    }
                }
            },
        }
    )
);

export default useCartStore;
